import { useCallback } from "react";
import { constructUrl, resolveApiUrl } from "~/utils/url/urlResolver";
import { AssistedServiceBarView } from "~/features/assistedService/components/AssistedServiceBar/AssistedServiceBarView";
import { useAssistedService } from "~/features/assistedService/hooks/useAssistedService";
import type { AutoSuggestionCustomer } from "~/features/assistedService/types";

const CUSTOMER_AUTOCOMPLETE_URL = "/assisted-service/autocomplete";

/**
 * Connected wrapper for the ASM bar. Wires {@link useAssistedService} (state query, RTK Query
 * mutations, timer, collapse cookie, close-URL strip) into the prop-driven {@link AssistedServiceBarView}.
 * Shell-owned: mounted unconditionally by {@link StorefrontShellFrame}; self-gates and renders
 * nothing while the state query loads or when ASM is inactive (not launched and no agent).
 */
export const AssistedServiceBar = () => {
  const vm = useAssistedService();

  const searchCustomers = useCallback(
    async (customerId: string, signal?: AbortSignal): Promise<AutoSuggestionCustomer[]> => {
      const url = constructUrl(CUSTOMER_AUTOCOMPLETE_URL, new URLSearchParams({ customerId }));
      const response = await fetch(resolveApiUrl(url), { credentials: "same-origin", signal });

      if (!response.ok) {
        throw new Error(`Failed to query ASM customers (${response.status})`);
      }

      return (await response.json()) as AutoSuggestionCustomer[];
    },
    [],
  );

  // Self-gate: render nothing while the state query loads or when ASM is inactive.
  if (vm.isLoading) {
    return null;
  }
  if (!vm.state.launched && !vm.state.agent) {
    return null;
  }

  return (
    <AssistedServiceBarView
      state={vm.state}
      remainingSeconds={vm.remainingSeconds}
      collapsed={vm.collapsed}
      alert={vm.alert}
      loginPending={vm.loginPending}
      personifyPending={vm.personifyPending}
      onLogin={vm.login}
      onPersonify={vm.personify}
      onPersonifyStop={vm.personifyStop}
      onLogout={vm.logout}
      onClose={vm.closeAssistedService}
      onResetTimer={vm.resetTimer}
      onToggleCollapse={vm.toggleCollapsed}
      onSearchCustomers={searchCustomers}
    />
  );
};
